import React, { useState, useEffect } from 'react'
import axiosInstance from '../utils/axiosInstance'

const FileManager = () => {
    const [files, setFiles] = useState([])
    const [selectedFile, setSelectedFile] = useState(null)
    const [uploading, setUploading] = useState(false)

    useEffect(() => {
        fetchFilesAndThumbs()
    }, [])

    const fetchFilesAndThumbs = async () => {
        try {
            const { data } = await axiosInstance.get('/data/files')
            const filesWithThumbs = await Promise.all(
                data.map(async (file) => {
                    try {
                        const thumbRes = await axiosInstance.get(
                            `/data/thumbnail/${encodeURIComponent(file.name)}`,
                            { responseType: 'blob' }
                        )
                        const blobUrl = URL.createObjectURL(thumbRes.data)
                        return { ...file, thumb: blobUrl }
                    } catch {
                        return { ...file, thumb: null }
                    }
                })
            )
            setFiles(filesWithThumbs)
        } catch (err) {
            console.error('Error fetching files:', err)
        }
    }

    const downloadFile = async (fileName) => {
        try {
            const response = await axiosInstance.get(
                `/data/download/${encodeURIComponent(fileName)}`,
                { responseType: 'blob' }
            )
            const url = URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            link.remove()
            URL.revokeObjectURL(url)
        } catch (err) {
            console.error('Error downloading file:', err)
        }
    }

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0])
    }

    const uploadFile = async () => {
        if (!selectedFile) return
        const formData = new FormData()
        formData.append('file', selectedFile)
        setUploading(true)
        try {
            await axiosInstance.post('/data/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            setSelectedFile(null)
            fetchFilesAndThumbs()
        } catch (err) {
            console.error('Error uploading file:', err)
        } finally {
            setUploading(false)
        }
    }

    return (
        <div className="p-4 max-w-8xl mx-auto bg-white border rounded shadow">
            <h2 className="text-2xl font-semibold mb-4">File Manager</h2>

            {/* Upload */}
            <div className="flex items-center mb-6 space-x-2">
                <input
                    type="file"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4
                     file:py-2 file:px-4
                     file:rounded-full file:border-0
                     file:text-sm file:font-semibold
                     file:bg-blue-50 file:text-blue-700
                     hover:file:bg-blue-100"
                />
                <button
                    onClick={uploadFile}
                    disabled={!selectedFile || uploading}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                    {uploading ? 'Uploading...' : 'Upload'}
                </button>
            </div>

            {/* File Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                {files.map(({ name, thumb }) => (
                    <div key={name} className="flex flex-col items-center">
                        <img
                            src={thumb || '/pdf-icon.svg'}
                            alt={`Thumbnail of ${name}`}
                            className="w-full h-auto object-cover rounded border cursor-pointer hover:shadow-lg hover:shadow-blue-300 transition duration-200 hover:scale-105"
                            onClick={() => downloadFile(name)}
                        />
                        <span className="mt-2 text-gray-800 break-all text-center">{name}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default FileManager
